import TableName from "../../../components/common/tableName";
import Button from "../../../../common/components/button";
import Input from "../../../components/common/input";
import { useState } from "react";
import { ReactComponent as RadioIcon } from "../../../assets/icons/radioIcon.svg";
import { usePostMember } from "../../../../common/hooks/console/member/usePostMember";
import SelectBox from "../../../components/common/selectBox";
import { useGetCommonCustomer } from "../../../../common/hooks/common/useGetCommonCustomer";
import useToastStore from "../../../store/useToastStore";
import { useSystemPopStore } from "../../../store/useSystemPopStore";

const NewMember = ({ onSuccess }: { onSuccess?: (memberIdx: number) => void }) => {
    const { addToast } = useToastStore();
    const { showPop } = useSystemPopStore();

    const [memberType, setMemberType] = useState<string>("public");
    const [customerName, setCustomerName] = useState<string>("");
    const [memberName, setMemberName] = useState<string>("");
    const [customerIdx, setCustomerIdx] = useState<number | null>(null);
    const [mobile, setMobile] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [memo, setMemo] = useState<string>("");
    const [customerNameError, setCustomerNameError] = useState<boolean>(false);
    const [memberNameError, setMemberNameError] = useState<boolean>(false);
    const [mobileError, setMobileError] = useState<boolean>(false);
    const [emailError, setEmailError] = useState<boolean>(false);
    const [passwordError, setPasswordError] = useState<boolean>(false);
    const [customerIdxError, setCustomerIdxError] = useState<boolean>(false);

    const { data: customerData } = useGetCommonCustomer();
    const customerOptions =
        customerData?.data.map(customer => ({
            value: customer.customer_idx.toString(),
            label: customer.customer_name,
        })) || [];

    const postMemberMutation = usePostMember(memberIdx => {
        addToast({
            type: "success",
            message: "회원정보가 신규등록되었습니다.",
        });
        onSuccess?.(memberIdx);
        resetForm();
    });

    const resetForm = () => {
        setCustomerIdx(null);
        setCustomerName("");
        setMemberName("");
        setMobile("");
        setEmail("");
        setPassword("");
        setMemo("");
        setCustomerIdxError(false);
        setCustomerNameError(false);
        setMemberNameError(false);
        setMobileError(false);
        setEmailError(false);
        setPasswordError(false);
    };

    const handleSubmit = () => {
        let hasError = false;
        if (memberType === "public") {
            if (!customerIdx) {
                setCustomerIdxError(true);
                hasError = true;
            } else {
                setCustomerIdxError(false);
            }
        }

        if (!customerName.trim()) {
            setCustomerNameError(true);
            hasError = true;
        } else {
            setCustomerNameError(false);
        }
        if (!memberName.trim()) {
            setMemberNameError(true);
            hasError = true;
        } else {
            setMemberNameError(false);
        }
        if (!mobile.trim()) {
            setMobileError(true);
            hasError = true;
        } else {
            setMobileError(false);
        }
        if (!email.trim()) {
            setEmailError(true);
            hasError = true;
        } else {
            setEmailError(false);
        }
        if (!password.trim()) {
            setPasswordError(true);
            hasError = true;
        } else {
            setPasswordError(false);
        }
        if (hasError) return;

        const memberData = {
            m_category: memberType === "public" ? 1 : 2,
            m_name: memberName,
            m_mobile: mobile,
            m_email: email,
            m_memo: memo,
            m_password: password,
            customer_idx: customerIdx ?? null,
            customer_name: memberType === "private" ? customerName : undefined,
        };

        postMemberMutation.mutate(memberData, {
            onError: (error: any) => {
                const errorMessage = error?.response?.data?.message || "알 수 없는 오류가 발생했습니다.";
                showPop({
                    title: errorMessage,
                    rightButton: undefined,
                    type: "negative",
                    onRightClick: () => {},
                });
            },
        });
    };

    return (
        <div className="bg-white shadow-custom rounded-xl px-3 py-5 flex flex-col gap-5">
            <p className="text-xl font-bold ">회원정보 신규등록</p>
            <div className="border-t-2 border-gray2">
                <div className="border-b border-gray4 flex">
                    <TableName className="h-12" required={true}>
                        회원구분
                    </TableName>
                    <div className="flex items-center gap-4 w-full p-2">
                        <label className="flex items-center w-1/2 gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="company"
                                value="public"
                                className="hidden peer"
                                onChange={() => setMemberType("public")}
                                checked={memberType === "public"}
                            />
                            <div className="h-5 w-5 rounded-full border-2 border-gray4 flex items-center justify-center peer-checked:border-primary2 peer-checked:bg-primary2">
                                {memberType === "public" && <RadioIcon className="w-full h-full fill-white" />}
                            </div>
                            <span className="text-gray2">회사 회원</span>
                        </label>
                        <label className="flex items-center gap-2 w-1/2 cursor-pointer">
                            <input
                                type="radio"
                                name="company"
                                value="private"
                                className="hidden peer"
                                onChange={() => setMemberType("private")}
                                checked={memberType === "private"}
                            />
                            <div className="w-5 h-5 rounded-full border-2 border-gray4 flex items-center justify-center peer-checked:border-primary2 peer-checked:bg-primary2">
                                {memberType === "private" && <RadioIcon className="w-full h-full fill-white" />}
                            </div>
                            <span className="text-gray2">개인 회원</span>
                        </label>
                    </div>
                </div>
                <div className="flex w-full">
                    <div className="border-b border-gray4 w-full flex items-center">
                        <TableName className="h-12" required={true}>
                            고객명
                        </TableName>
                        {/* {memberType === "public" ? ( */}
                        <SelectBox
                            height="max-h-[500px]"
                            length={10}
                            searchable={true}
                            filterable={true}
                            className="w-full px-2"
                            options={customerOptions}
                            label={customerName}
                            value={customerIdx?.toString()}
                            status={customerIdxError ? "error" : undefined}
                            onChange={value => {
                                const selectedCustomer = customerOptions.find(option => option.value === value);
                                setCustomerIdx(Number(value));
                                setCustomerName(selectedCustomer?.label || "");
                                setCustomerIdxError(false);
                            }}
                        />
                        {/* ) : (
                            <Input
                                className="px-2"
                                // placeholder="30자"
                                value={customerName}
                                onChange={e => {
                                    const inputText = e.target.value;
                                    // if (inputText.length <= 30) {
                                    // setCustomerNameError(false);
                                    setCustomerName(inputText);
                                    // }
                                }}
                                status={customerNameError ? "error" : undefined}
                            />
                        )} */}
                    </div>
                    <div className="border-b border-gray4 w-full flex items-center">
                        <TableName className="h-12" required={true}>
                            회원명
                        </TableName>
                        <Input
                            status={memberNameError ? "error" : undefined}
                            className="px-2"
                            // placeholder="20자"
                            value={memberName}
                            onChange={e => {
                                const inputText = e.target.value;
                                // if (inputText.length <= 20) {
                                // setMemberNameError(false);
                                setMemberName(inputText);
                                // }
                            }}
                        />
                    </div>
                </div>

                <div className="flex w-full">
                    <div className="border-b border-gray4 w-full flex items-center">
                        <TableName className="h-12" required={true}>
                            연락처
                        </TableName>
                        <Input
                            status={mobileError ? "error" : undefined}
                            className="px-2"
                            value={mobile}
                            onChange={e => {
                                setMobileError(false);
                                const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                                let formattedNumber = "";

                                if (onlyNumbers.length <= 3) {
                                    formattedNumber = onlyNumbers;
                                } else if (onlyNumbers.length <= 7) {
                                    formattedNumber = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3)}`;
                                } else {
                                    formattedNumber = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3, 7)}-${onlyNumbers.slice(7, 11)}`;
                                }

                                setMobile(formattedNumber);
                            }}
                        />
                    </div>
                    <div className="border-b border-gray4 w-full flex items-center">
                        <TableName className="h-12" required={true}>
                            이메일
                        </TableName>
                        <Input
                            status={emailError ? "error" : undefined}
                            className="px-2"
                            value={email}
                            onChange={e => {
                                setEmailError(false);
                                setEmail(e.target.value);
                            }}
                        />
                    </div>
                </div>
                <div className="flex items-center w-1/2">
                    <TableName className="h-12" required={true}>
                        비밀번호
                    </TableName>
                    <Input
                        placeholder="영문 숫자 6자리 이상"
                        status={passwordError ? "error" : undefined}
                        className="px-2"
                        value={password}
                        onChange={e => {
                            setPasswordError(false);
                            setPassword(e.target.value);
                        }}
                    />
                </div>
                <div className="border-y border-gray4 flex items-center">
                    <TableName className="h-[120px]" required={false}>
                        메모 입력
                    </TableName>
                    <textarea
                        className="p-2 m-2 border outline-0 h-[100px] w-full rounded-lg border-gray4 text-sm resize-none"
                        value={memo}
                        onChange={e => setMemo(e.target.value)}
                    />
                </div>
            </div>
            <div className="flex w-full gap-2">
                <Button size="L" className="w-full" onClick={resetForm}>
                    취소
                </Button>
                <Button size="L" status="regist" className="w-full" onClick={handleSubmit}>
                    저장
                </Button>
            </div>
        </div>
    );
};

export default NewMember;
